import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const DEFAULT_WIDTH = 144;
const DEFAULT_HEIGHT = 92;
export const useDrawerStore = create(devtools((set) => ({
  drawerOpen: false,
  setDrawerOpen: (open) => set({ drawerOpen: open }),
  collectionDrawerOpen: false,
  setCollectionDrawerOpen: (open) => set({ collectionDrawerOpen: open }),
  selectedTouchPoint: null,
  setSelectedTouchPoint: (touchPoint) => set({ selectedTouchPoint: touchPoint }),
  activeCabinet: null,
  setActiveCabinet: (cabinet) => set({ activeCabinet: cabinet }),
  selectedCollection: null,

  // Collection Matching State (HPP-5621)
  matchingCollections: null, // Array of matching collections from API
  matchingSkuHash: null, // Hash of original SKUs used for cache comparison
  originalSkuSet: null, // Original SKUs captured on first match (baseline for cache)
  isMatchingMode: false, // Whether drawer is in matching mode
  isMatchingLoading: false, // Loading state for matching API
  matchingError: null, // Error message if matching fails

  // Collection Matching Actions
  setMatchingCollections: (collections, hash, originalSkus) => set({
    matchingCollections: collections,
    matchingSkuHash: hash,
    originalSkuSet: originalSkus,
    isMatchingMode: true,
    isMatchingLoading: false,
    matchingError: null
  }),
  setMatchingLoading: (loading) => set({
    isMatchingLoading: loading,
    // When starting to load, also set matching mode to true AND open drawer atomically
    // This prevents race conditions where WallDesigner sees collectionDrawerOpen=true before isMatchingMode=true
    ...(loading ? { isMatchingMode: true, collectionDrawerOpen: true } : {})
  }),
  setMatchingError: (error) => set({
    matchingError: error,
    isMatchingLoading: false
  }),
  clearMatchingCollections: () => {
    return set({
      matchingCollections: null,
      matchingSkuHash: null,
      originalSkuSet: null,
      isMatchingMode: false,
      isMatchingLoading: false,
      matchingError: null
    });
  },
  // Close matching mode but PRESERVE the cache (originalSkuSet, matchingCollections)
  // Used after successfully applying a collection - cache remains valid for next "Change Collection" click
  closeMatchingMode: () => set({
    isMatchingMode: false,
    isMatchingLoading: false,
    matchingError: null,
    collectionDrawerOpen: false
  }),
  // Open matching drawer atomically - sets isMatchingMode and collectionDrawerOpen together
  // Prevents race condition where WallDesigner drawer opens before isMatchingMode is set
  openMatchingDrawer: () => set({
    isMatchingMode: true,
    collectionDrawerOpen: true
  }),

  getLeftViewImageUrl: (product) => {
    if (product?.media?.images?.length > 0) {
      const letViewImage = product.media.images.find((img) => img.type === 'IMAGE_LEFT_VIEW');

      if (letViewImage) {
        return letViewImage.url.replace('<SIZE>', '400');
      }
      return product.media.images[0].url.replace('<SIZE>', '400');
    }
    return null;
  },

  setSelectedCollection: (collection) => set((state) => ({
    selectedCollection: { ...collection,
      leftViewImageUrl: collection.leftViewImageUrl || state.getLeftViewImageUrl(collection) }
  })),
  collections: null,
  setCollections: (collections) => set({ collections })
}), { name: 'DrawerStore' }));

export const useCabinetStore = create((set) => ({
  activeCabinet: null,
  setActiveCabinet: (cabinet) => set({ activeCabinet: cabinet }),
  patchActiveCabinet: (patchFn) => set((state) => ({
    activeCabinet: patchFn(state.activeCabinet),
  })),
  selectedCabinet: null,
  setSelectedCabinet: (cabinet) => set({ selectedCabinet: cabinet }),
}));

export const useViewportStore = create((set) => ({
  viewportInfo: {
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    finalZoom: 1,
    baseZoom: 1,
    manualZoom: false,
    scrollOffset: { scrollLeft: 0, scrollTop: 0 }
  },
  setViewportInfo: (info) => set({ viewportInfo: info }),
  setScrollOffset: (scrollOffset) => set((state) => ({
    viewportInfo: {
      ...state.viewportInfo,
      scrollOffset,
    },
  })),
  patchViewport: (patchFn) => set((state) => ({ viewportInfo: patchFn(state.viewportInfo) })),
}));
